<template>
  <div :class="$style.container">
    <h3>キャンセル理由をお選びください。</h3>
    <DolphinSelector
      v-model="selectedCancelReason"
      :options="cancelBookingOptions"
      placeholder="選択してください"
    />
    <div :class="$style.note">
      ※キャンセルすると、連携している外部カレンダーの予定は削除されます。
    </div>
    <div :class="$style.buttons">
      <FlatButton variant="secondary" @click="emit('cancel')">
        閉じる
      </FlatButton>
      <FlatButton
        :disabled="!selectedCancelReason || isSubmitting"
        @click="handleSubmit"
      >
        送信する
      </FlatButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { BookingCancelReason } from '@/types/schema.generated'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useAlert } from '@/libs/alertPortal'
import { wait } from '@/libs/utilities/wait'
import { useCancelBookingMutation } from './CancelBookingModal.generated.ts'

const props = defineProps<{
  bookingGUID: string
}>()

const emit = defineEmits<{
  (event: 'submit'): void
  (event: 'cancel'): void
}>()

const cancelBookingOptions = [
  {
    text: 'プランナーによるキャンセル',
    value: BookingCancelReason.EntranceCancelled,
  },
  {
    text: 'ユーザーによるキャンセル',
    value: BookingCancelReason.UserCancelled,
  },
]

const selectedCancelReason = ref<BookingCancelReason | null>(null)

const isSubmitting = ref(false)

const { mutate: cancelBookingMutate } = useCancelBookingMutation()

const alert = useAlert()

const handleSubmit = async () => {
  if (!selectedCancelReason.value) {
    return
  }

  isSubmitting.value = true

  try {
    cancelBookingMutate({
      guid: props.bookingGUID,
      cancelReason: selectedCancelReason.value,
    })
    alert.success('キャンセルしました')
    await wait(1000)
    emit('submit')
  } catch (e) {
    console.error(e)
    alert.error('エラーが発生しました。再度お試しください。')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" module>
.container {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.note {
  color: $color-text-and-object-accent-red;
}

.buttons {
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: flex-end;
}
</style>
